/**
 * Service to handle feature flag checks
 * Centralizes feature flag logic for the application
 */
import { SplitFeatureFlags } from 'bk-web-app/features';

const SplitFactory = require('@splitsoftware/splitio').SplitFactory;
const { addFeatureFlagEvaluation } = require('../../../app/dashboard/datadogRUMSetup');

export default class FeatureFlagService {
  static get $inject() {
    return ['User', 'Client', '$q'];
  }

  constructor(User, Client, $q) {
    this.User = User;
    this.Client = Client;
    this.$q = $q;
    this.clientCache = null;
    this.clientPromise = null;

    // eslint-disable-next-line new-cap
    const factory = SplitFactory({
      core: {
        authorizationKey: window.BENCH ? window.BENCH.vendor.split.authorizationKey : '',
        key: this.User.getClientId(),
      },
      impressionListener: {
        logImpression: impressionData => {
          addFeatureFlagEvaluation(
            impressionData.impression.feature,
            impressionData.impression.treatment,
          );
        },
      },
    });
    this.featureFlagClient = factory.client();

    this.sdkReadyPromise = new Promise(resolve => {
      this.featureFlagClient.on(this.featureFlagClient.Event.SDK_READY, () => {
        resolve();
      });
    });
  }

  /**
   * Checks if a specified feature flag is enabled
   * @param {string} featureFlag - The feature flag to check (use SplitFeatureFlags enum)
   * @param {object} context - Additional context to provide to the flag check
   * @returns {boolean} - Whether the feature is enabled
   */
  isFeatureEnabled(featureFlag, context = {}) {
    // Add client ID to context if not already provided
    const checkContext = {
      clientId: this.User.getClientId(),
      ...context,
    };

    const treatment = this.featureFlagClient.getTreatmentsWithConfig([featureFlag], checkContext);

    return treatment[featureFlag]?.treatment === 'on';
  }

  /**
   * Checks if the message formatting feature is enabled
   * @param {object} context - Additional context to provide to the flag check
   * @returns {boolean} - Whether message formatting is enabled
   */
  isMessageFormattingEnabled(context = {}) {
    return this.isFeatureEnabled(SplitFeatureFlags.MESSAGE_FORMATTING_MILESTONE_1, context);
  }

  /**
   * Checks if the SMS message feed feature is enabled
   * @param {object} context - Additional context to provide to the flag check
   * @returns {boolean} - Whether SMS message feed is enabled
   */
  isSmsMessageFeedEnabled(context = {}) {
    return this.isFeatureEnabled(SplitFeatureFlags.SMS_MESSAGE_FEED, context);
  }

  /**
   * Fetches client data (cached) for use in feature flag checks
   * @returns {Promise} - Promise that resolves to the client instance
   * @private
   */
  getClient() {
    if (this.clientCache) {
      return this.$q.resolve(this.clientCache);
    }

    if (this.clientPromise) {
      return this.clientPromise;
    }

    const clientId = this.User.getClientId();
    if (!clientId) {
      return this.$q.reject('No client ID available');
    }

    this.clientCache = new this.Client(clientId);
    this.clientPromise = this.clientCache
      .$fetch()
      .then(() => {
        this.clientPromise = null; // Clear promise once resolved
        return this.clientCache;
      })
      .catch(err => {
        this.clientCache = null; // Clear cache on error
        this.clientPromise = null;
        return this.$q.reject(err);
      });

    return this.clientPromise;
  }

  /**
   * Checks if a feature flag is enabled with full context including bookkeeper IDs
   *
   * @param {string} featureFlag - The feature flag to check (use SplitFeatureFlags enum)
   * @param {object} additionalContext - Additional context to merge with bookkeeper data
   * @returns {Promise<boolean>} - Promise that resolves to whether the feature is enabled
   */
  isFeatureEnabledWithBookkeepers(featureFlag, additionalContext = {}) {
    return Promise.all([this.sdkReadyPromise, this.getClient()])
      .then(([, client]) => {
        const bookkeepers = client.assignedPersons?.bookkeepers || {};

        const attributes = {
          id: this.User.getClientId(),
          bookkeeperId: bookkeepers.primary?.id,
          secondaryBookkeeperId: bookkeepers.secondary?.id,
          tertiaryBookkeeperId: bookkeepers.tertiary?.id,
          ...additionalContext,
        };

        const treatment = this.featureFlagClient.getTreatmentsWithConfig([featureFlag], attributes);
        return treatment[featureFlag]?.treatment === 'on';
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to check feature flag with bookkeeper context:', err);
        return false;
      });
  }

  /**
   * Explicitly destroy the client when service is no longer needed
   * This prevents memory leaks and is important for clean shutdown
   */
  destroy() {
    if (this.featureFlagClient) {
      this.featureFlagClient.destroy();
    }
  }
}
